import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { CommonSelect, type SelectProps } from 'components/common';
import { useInfiniteFacebookPages } from '../api';
import type { FacebookPage } from '../types';
import { useInView } from 'react-intersection-observer';
import { useFormContext } from 'react-hook-form';
import type { SelectChangeEvent } from '@mui/material';
import type { ListResponse } from 'types/shared';
import { trackCabinetQueryError } from 'lib/datadog';

interface SelectFacebookPageAttrs {
  open: boolean;
  multiples: boolean;
  placeholder: string;
  name: 'defaultFacebookPageId' | 'additionalFacebookPagesIds';
}

type SelectFacebookPageProps = Partial<
  Pick<SelectProps, 'handleChange' | 'inputLabel' | 'onClose' | 'required'> &
  SelectFacebookPageAttrs
>;

const SelectFacebookPage: FC<SelectFacebookPageProps> =
  ({
     handleChange,
     name,
     inputLabel,
     multiples,
     placeholder,
     onClose,
     open,
     required,
   }) => {
    const { ref, inView } = useInView({
      threshold: 0,
    });
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, error } =
      useInfiniteFacebookPages();
    const { watch, setValue } = useFormContext();

    const defaultFacebookPageId = watch('defaultFacebookPageId') as number;
    const additionalFacebookPagesIds = watch(
      'additionalFacebookPagesIds',
    ) as number[];

    useEffect(() => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    useEffect(() => {
      if (!isError || !error) {
        return;
      }

      trackCabinetQueryError(error, 'facebook', 'facebook_page');
    }, [isError, error]);

    const options = useMemo(() => {
      if (!data) return [];

      return data.pages.flatMap((page: ListResponse<FacebookPage>) =>
        page.data
          .filter((facebookPage: FacebookPage) => {
            const id = Number(facebookPage.id);
            if (name === 'additionalFacebookPagesIds') {
              return id !== defaultFacebookPageId;
            }
            return true;
          })
          .map(({ facebook_page_name, id }: FacebookPage) => ({
            label: facebook_page_name,
            value: Number(id),
          })),
      );
    }, [data, name, defaultFacebookPageId]);

    const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
      const value = event.target.value;

      if (
        name === 'defaultFacebookPageId' &&
        typeof value === 'number' &&
        additionalFacebookPagesIds?.includes(value)
      ) {
        setValue(
          'additionalFacebookPagesIds',
          additionalFacebookPagesIds.filter((id) => id !== value),
        );
      }
      if (
        name === 'additionalFacebookPagesIds' &&
        Array.isArray(value) &&
        value.includes(defaultFacebookPageId)
      ) {
        setValue('defaultFacebookPageId', 0);
      }
      handleChange?.(event);
    };

    return (
      <CommonSelect
        name={name ?? 'facebookPage'}
        inputLabel={inputLabel ?? 'Facebook page'}
        options={options}
        handleChange={handleSelectChange}
        placeholder={placeholder}
        multiples={multiples}
        onClose={onClose}
        open={open}
        loading={isFetchingNextPage}
        loadMoreRef={ref}
        required={required}
      />
    );
  };

export default SelectFacebookPage;
